import React, { createContext, useContext, useState } from 'react';
import Toast from '../components/Toast';
import { ERROR_MESSAGES, toUserMessage } from '../utils/userErrors';
import { isNotificationsMuted } from '../utils/notificationPreferences';
import { noteDisplayedError } from '../utils/clientErrorReporter';

const ToastContext = createContext();

// Errors/warnings need more time to be read than a success tick.
const DEFAULT_DURATION = { error: 7000, warning: 5000, info: 4000, success: 3000 };

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'info', duration, title = '') => {
    const id = Date.now() + Math.random();
    const text = type === 'error' ? toUserMessage(message, ERROR_MESSAGES.request) : message;
    // Remember the exact text the user sees so a failed API call can be reported with it (logging only).
    if (type === 'error') noteDisplayedError(text);
    const newToast = { id, message: text, type, duration: duration ?? DEFAULT_DURATION[type] ?? 3000, title };
    // The same message is not stacked twice while it is still on screen.
    setToasts((prev) =>
      prev.some((t) => t.message === text && t.type === type && t.title === title)
        ? prev
        : [...prev, newToast]
    );
    return id;
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  // "Mute notifications" silences every non-error toast app-wide; errors always show.
  const unlessMuted = (type) => (message, duration, title) =>
    isNotificationsMuted() ? null : addToast(message, type, duration, title);

  const showSuccess = unlessMuted('success');
  const showError = (message, duration, title) => addToast(message, 'error', duration, title);
  const showWarning = unlessMuted('warning');
  const showInfo = unlessMuted('info');

  return (
    <ToastContext.Provider
      value={{
        addToast,
        removeToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
      }}
    >
      {children}
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id}
          message={toast.message}
          title={toast.title}
          type={toast.type}
          duration={toast.duration}
          stackIndex={index}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
};
